import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { debounce, first, merge, Subject, switchMap, timer } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { ConfirmationModalService, InformationModalService } from 'shared-ui';
import { translate } from 'translations-utils';
import { ConfirmationModalConfig, InformationModalConfig } from 'shared-utils';
import { AuthService, RefreshTokenService } from 'auth';

export const SESSION_IDLE_TERMINATE_TIME_KEY = 'sessionIdleTerminateTimeMs';
export const SESSION_EXPIRE_TIME_KEY = 'sessionExpireTimeMs';

@Injectable({ providedIn: 'root' })
export class SessionHandlerService {
  private authService = inject(AuthService);
  private refreshTokenService = inject(RefreshTokenService);
  private informationModalService = inject(InformationModalService);
  private confirmationModalService = inject(ConfirmationModalService);

  private readonly sessionIdleTimeMs: number = environment.config.sessionIdleTimeMs;
  private readonly sessionExpireTimeMs: number = environment.config.sessionExpireTimeMs;
  private readonly sessionLogoutDelayMs = 5 * 60 * 1000;
  private readonly sessionIdleModalTimeHook: Subject<number | undefined> = new Subject<number | undefined>();

  constructor() {
    this.initTokenRefreshListener();
    this.initIdleModalTimeHookListener();
  }

  resetIdleModalTimeHook(): void {
    this.sessionIdleModalTimeHook.next(this.getModalDelayMs(this.getSessionIdleTerminateTimeMs()));
  }

  setSessionExpireModalListener(): void {
    const delay: number | undefined = this.getModalDelayMs(this.getSessionExpireTimeMs());
    const informationModalConfig: InformationModalConfig = {
      header: translate('portal.session-expire-modal.header:Warning'),
      message: translate('portal.session-expire-modal.message:Your session is about to expire.'),
      additionalMessage: translate('portal.session-expire-modal.additionalMessage:Please save any unsaved work.'),
      status: 'warning',
    };

    if (delay === undefined) {
      return;
    }

    timer(delay)
      .pipe(
        switchMap(() => this.informationModalService.open(informationModalConfig)),
        take(1)
      )
      .subscribe();
  }

  updateIdleTerminateTime(): void {
    localStorage.setItem(SESSION_IDLE_TERMINATE_TIME_KEY, String(Date.now() + this.sessionIdleTimeMs));
  }

  updateExpireTime(): void {
    localStorage.setItem(SESSION_EXPIRE_TIME_KEY, String(Date.now() + this.sessionExpireTimeMs));
  }

  private getSessionIdleTerminateTimeMs(): string | null {
    return localStorage.getItem(SESSION_IDLE_TERMINATE_TIME_KEY);
  }

  private getSessionExpireTimeMs(): string | null {
    return localStorage.getItem(SESSION_EXPIRE_TIME_KEY);
  }

  private getModalDelayMs(actionTimeMs: string | null): number | undefined {
    if (actionTimeMs === null) {
      return undefined;
    }

    return +actionTimeMs - Date.now() - this.sessionLogoutDelayMs;
  }

  private initTokenRefreshListener(): void {
    this.refreshTokenService.tokenRefresh$.subscribe(() => {
      this.updateIdleTerminateTime();
      this.resetIdleModalTimeHook();
    });
  }

  private initIdleModalTimeHookListener(): void {
    const modalConfig: ConfirmationModalConfig = {
      header: translate('portal.session-idle-modal.header'),
      message: translate('portal.session-idle-modal.message'),
      additionalMessage: translate('portal.session-idle-modal.additionalMessage'),
      acceptButton: { label: translate('portal.session-idle-modal.button.accept') },
      cancelButton: { label: translate('portal.session-idle-modal.button.cancel') },
      status: 'warning',
    };

    this.sessionIdleModalTimeHook
      .pipe(
        filter((value: number | undefined) => value !== undefined),
        debounce((msTillSessionIdleModal: number) => timer(msTillSessionIdleModal)),
        switchMap(() =>
          merge(
            this.confirmationModalService.open(modalConfig),
            timer(this.sessionLogoutDelayMs).pipe(map(() => false))
          ).pipe(first())
        ),
        switchMap((shouldProlong: boolean) =>
          shouldProlong ? this.refreshTokenService.refreshToken() : this.authService.logout()
        )
      )
      .subscribe();
  }
}
